import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import { TrendingUp } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const LINE_COLORS = ["#6366f1", "#e11d48", "#0ea5e9", "#16a34a", "#d97706",
  "#7c3aed", "#db2777", "#0d9488", "#65a30d", "#2563eb"];
const BUCKETS = [
  { key: "overdue", color: "#e11d48", label: "Overdue" },
  { key: "due_week", color: "#d97706", label: "Due this week" },
  { key: "later", color: "var(--bar)", label: "Later" },
];

// Static recharts style objects hoisted out so they aren't re-created every render.
const CHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#71717a" };
const X_AXIS_LINE = { stroke: "#e4e4e7" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 4, border: "1px solid #e4e4e7" };
const LEGEND_STYLE = { fontSize: 11 };
const LINE_DOT = { r: 3 };
const LINE_ACTIVE_DOT = { r: 5 };

export const WorkloadTrends = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard/workload-trends").then((r) => setD(r.data)).catch(() => {});
  }, []);

  // Unique display label per person (first name, falling back to full name on collision).
  const labels = useMemo(() => {
    const map = {}; const seen = {};
    (d?.persons || []).forEach((p) => {
      const first = p.name.split(" ")[0];
      const label = seen[first] ? p.name : first;
      seen[first] = true;
      map[p.user_id] = label;
    });
    return map;
  }, [d]);

  const chartData = useMemo(() => {
    if (!d) return [];
    return d.weeks.map((w) => {
      const row = { week: w.week.replace(/^\d+-/, "") };
      d.persons.forEach((p) => { row[labels[p.user_id]] = w.counts[p.user_id] ?? 0; });
      return row;
    });
  }, [d, labels]);

  if (!d) return null;

  const hasTrend = d.weeks.length > 0 && d.persons.length > 0;
  const bd = d.this_week;
  const maxTotal = bd.reduce((m, r) => Math.max(m, r.total), 0);

  return (
    <div className="bg-white border border-zinc-200 mb-10" data-testid="workload-trends">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-zinc-500" />
        <h3 className="font-heading font-bold">Workload Trends</h3>
        <span className="text-xs text-zinc-400 hidden sm:block">Open tasks per person — this week &amp; over time</span>
      </div>

      {/* This-week breakdown */}
      <div className="p-5 space-y-2.5 border-b border-zinc-100" data-testid="workload-trends-thisweek">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">This week</p>
        {bd.length === 0 && <p className="text-sm text-zinc-400">No open tasks assigned.</p>}
        {bd.map((p) => (
          <button key={p.user_id} onClick={() => navigate(`/tasks?assignee=${p.user_id}`)}
            className="w-full flex items-center gap-3 text-left" data-testid={`trend-person-${p.user_id}`}>
            <Avatar id={p.user_id} name={p.name} size={24} />
            <span className="w-24 sm:w-28 shrink-0 text-sm font-medium truncate">{p.name.split(" ")[0]}</span>
            <div className="flex-1 h-6 bg-zinc-100 rounded-sm overflow-hidden flex">
              {BUCKETS.map(({ key, color }) => {
                const val = p[key];
                if (!val) return null;
                const pct = maxTotal ? (val / maxTotal) * 100 : 0;
                return <div key={key} style={{ width: `${pct}%`, background: color }} className="h-full" title={`${key}: ${val}`} />;
              })}
            </div>
            <span className="w-7 text-right font-heading font-black text-zinc-900">{p.total}</span>
          </button>
        ))}
        {bd.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 pt-1 text-[11px] text-zinc-500">
            {BUCKETS.map(({ key, color, label }) => (
              <span key={key} className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Weekly trend line */}
      <div className="p-5" data-testid="workload-trends-chart">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-3">Open tasks over time</p>
        {hasTrend ? (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis dataKey="week" tick={AXIS_TICK} axisLine={X_AXIS_LINE} tickLine={false} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                {d.persons.map((p, i) => (
                  <Line key={p.user_id} type="monotone" dataKey={labels[p.user_id]}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={LINE_DOT} activeDot={LINE_ACTIVE_DOT} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            {d.weeks.length < 2 && (
              <p className="text-xs text-zinc-400 mt-2">Trends build up week over week — check back next week to watch the lines grow.</p>
            )}
          </>
        ) : (
          <p className="text-sm text-zinc-400">No history yet — this chart starts filling in from this week onward.</p>
        )}
      </div>
    </div>
  );
};

export default WorkloadTrends;
